import { createDecorator } from 'vue-class-component'
import * as functionUtils from '@ustra/core/src/utils/function-utils'
import * as coreUtils from '@ustra/core/src/utils/core-utils'

export interface OnErrorOption {
  /**
   * 통지 유형
   * @default 'alert'
   */
  notifyType?: 'alert' | 'toast' | 'redirect'

  /**
   * 예외 throw 여부
   * @default false
   */
  throwError?: boolean

  /**
   * 노출 메시지
   */
  message?: ((this: Vue, e: Error) => string) | string

  /**
   * 에러 시 처리할 Action
   */
  onError?: (this: Vue, e: Error) => void | Promise<void>

  /**
   * 로깅 여부
   * @default true
   */
  logging?: boolean

  /**
   * http status code
   * @default 500
   */
  statusCode?: number

  /**
   * error redirect path
   */
  path?: string
}

const defaultOption: OnErrorOption = {
  notifyType: 'alert',
  throwError: false,
  logging: true,
  statusCode: 500,
}

const notify = async (vm: Vue, option: OnErrorOption, e: Error) => {
  let message = ''

  if (!option.message) {
    message = e.message
  } else if (typeof option.message === 'function') {
    message = option.message.call(vm, e)
  } else if (typeof option.message === 'string') {
    message = option.message
  }

  if (option.logging) {
    vm.$logger().error('onerror', e)
  }

  if (option.notifyType === 'alert') {
    await vm.$nextTick()
    await alert(message)

    if (option.onError) {
      await option.onError.call(vm, e)
    }
  } else if (option.notifyType === 'toast') {
    await vm.$nextTick()
    await window.toast(message)
    if (option.onError) {
      await option.onError.call(vm, e)
    }
  } else if (option.notifyType === 'redirect') {
    if (option.onError) {
      await option.onError.call(vm, e)
    }
    vm.$nuxt.error({ statusCode: option.statusCode, message, path: option.path })
  }
}

export const OnError = (option?: OnErrorOption) => {
  option = coreUtils.deepMerge({}, defaultOption, option)

  return createDecorator((options, key) => {
    const originalMethod = options.methods[key]

    // @ts-ignore
    options.methods[key] = functionUtils.wrap(originalMethod, function (next) {
      let result
      try {
        result = next()

        if (functionUtils.isPromise(result)) {
          return (result as Promise<any>).catch(async (e) => {
            await notify(this, option, e)
          })
        }
      } catch (e) {
        notify(this, option, e)
      }

      return result
    })
  })
}
